import React, { useEffect, useState } from "react";

import { showNotification } from "Helpers/Toast";
import { htmlToText } from "Helpers/Util";

import UseAuth from "Hooks/UseAuth";
import { useFetch } from "Hooks/useFetch";

import PopUpModal from "Components/Common/PopUp";

// Intervalo do polling de alertas (D5 da parte 4). Constante no topo, como no
// useVersionCheck — o número no meio do efeito é o que ninguém acha depois.
const ALERTS_INTERVAL_MS = 60 * 1000;

export const NotificationContext = React.createContext({
  notifications: null,
  regularNotifications: null,
  popups: [],
  alerts: [],
  hasAlerts: false,
  hasPendingNotifications: false,
  page: 1,
  totalRows: 0,
  getNotifications: () => {},
  readNotification: () => {},
  readNotifications: [],
});

const NotificationProvider = ({ children }) => {
  const { authorized, parsedJWT } = UseAuth();
  const { fetchData } = useFetch();

  /*
   * ⚠️ NÃO usar o `isAdmin` do UseAuth aqui: o efeito de Contexts/Auth.js só
   * faz `setIsAdmin(true)` — nunca volta para false ao trocar de usuário. O
   * perfil vem direto do JWT, que está disponível desde o boot.
   */
  const isAdminUser = parsedJWT?.user?.profile_type_id === 1;

  const [page, setPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);

  const [notifications, setNotifications] = useState(null);
  const [regularNotifications, setRegularNotifications] = useState(null);
  const [readNotifications, setReadNotifications] = useState([]);

  const [hasPendingNotifications, setHasPendingNotifications] = useState(false);

  const [popUps, setPopUps] = useState([]);
  const [popUpIndex, setPopUpIndex] = useState(0);

  // Alertas do servidor (parte 4). Hoje só existe o de aprovações pendentes,
  // que é do admin — o usuário comum vê o modal vazio (D5).
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    if (!authorized || !isAdminUser) return setAlerts([]);

    let stopped = false;

    const check = async () => {
      if (stopped) return;

      const response = await fetchData({
        method: "get",
        path: "/notifications/alerts",
        config: {
          // GET sem body: sem isto o axios recebe o body como config.
          use_body_in_request: false,
          // O alerta é acessório: não pode piscar loading nem estourar toast de
          // erro a cada minuto se a rede cair.
          use_loading: false,
          use_global_loading: false,
          show_request_error_message: false,
        },
      });

      if (!stopped) setAlerts(response?.data?.alerts || []);
    };

    check();

    const intervalId = setInterval(check, ALERTS_INTERVAL_MS);

    return () => {
      stopped = true;
      clearInterval(intervalId);
    };
    // eslint-disable-next-line
  }, [authorized, isAdminUser]);

  useEffect(() => {
    if (!authorized) return;

    if (notifications) return createToastOrPopup(notifications);

    getNotifications();

    // eslint-disable-next-line
  }, [window.location.pathname]);

  useEffect(() => {
    if (!notifications) return;

    if (readNotifications.length === notifications["regular"].length) {
      setHasPendingNotifications(false);
    }
  }, [readNotifications]);

  const getNotifications = async (page = 1) => {
    const response = await fetchData({
      method: "get",
      path: `/notifications/list-active-notifications`,
      queryParams: {
        page,
        page_size: 10,
        get_total_rows: true,
      },
    });

    if (!response?.data) return;
    const data = response.data;

    setNotifications(data?.notifications);

    setRegularNotifications(
      page === 1
        ? data?.notifications["regular"]
        : [...regularNotifications, ...data.notifications["regular"]],
    );
    setReadNotifications(data?.read_notifications);

    setPage(page);
    setTotalRows(data?.total_rows);
    setHasPendingNotifications(data?.has_pending_notifications);

    createToastOrPopup(data?.notifications);
  };

  const readNotification = async (id, type = "regular") => {
    if (readNotifications.includes(id)) return;

    await fetchData({
      method: "put",
      path: `/notifications/read-notification/:id`,
      params: { id },
    });

    if (type === "popup") {
      setPopUpIndex(popUpIndex + 1);
    }

    setReadNotifications((prev) => [...prev, id]);
  };

  const shouldShowNotification = (notification) =>
    (notification.route
      ? window.location.pathname === notification.route
      : true) &&
    !readNotifications.includes(notification.id) &&
    !notification.read;

  const createToastOrPopup = (notifications) => {
    for (const notification of notifications["toast"]) {
      if (shouldShowNotification(notification)) {
        const text = htmlToText(notification.description);

        showNotification("warning", notification.title, text);
        readNotification(notification.id);
      }
    }

    const new_popups = [];

    for (const notification of notifications["popup"]) {
      if (shouldShowNotification(notification)) {
        new_popups.push(notification);
      }
    }

    setPopUps(new_popups);
  };

  const contextValue = {
    notifications,
    regularNotifications,
    popups: popUps,
    alerts,
    hasAlerts: alerts.length > 0,
    hasPendingNotifications,
    page,
    totalRows,
    getNotifications,
    readNotification,
    readNotifications,
  };

  return (
    <>
      <NotificationContext.Provider value={contextValue}>
        {popUps.length ? (
          <PopUpModal
            popUps={popUps}
            read={readNotification}
            index={popUpIndex}
          />
        ) : null}
        {children}
      </NotificationContext.Provider>
    </>
  );
};

export default NotificationProvider;
